import { Navigate } from 'react-router-dom';
import { useUserRole } from '@/hooks/useUserRole';
import type { UserType } from '@/types/database';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { ShieldAlert } from 'lucide-react';

// Gerarchia ruoli per controllo accesso
const ROLE_HIERARCHY: Record<UserType, number> = {
  utente: 1,
  operatore_business: 2,
  membro_forze_armate: 2,
  blog_editor: 3,
  admin: 4,
};

interface RoleBasedRouteProps {
  children: React.ReactNode;
  requiredRole: UserType | UserType[];
  fallback?: React.ReactNode;
  useHierarchy?: boolean; // Se true, controlla la gerarchia invece del match esatto
}

export const RoleBasedRoute = ({ 
  children, 
  requiredRole, 
  fallback,
  useHierarchy = true,
}: RoleBasedRouteProps) => {
  const { userRole, loading } = useUserRole();

  // Show loading spinner while checking role
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
      </div>
    );
  }

  // Check if user has required role
  let hasRequiredRole = false;

  if (userRole) {
    if (useHierarchy) {
      // Controllo gerarchico: l'utente può accedere se il suo livello è >= a quello richiesto
      const userLevel = ROLE_HIERARCHY[userRole];
      
      if (Array.isArray(requiredRole)) {
        // Per array, basta che l'utente abbia livello >= al minimo richiesto
        const minRequiredLevel = Math.min(...requiredRole.map(r => ROLE_HIERARCHY[r]));
        hasRequiredRole = userLevel >= minRequiredLevel;
      } else {
        hasRequiredRole = userLevel >= ROLE_HIERARCHY[requiredRole];
      }
    } else {
      // Controllo esatto: l'utente deve avere esattamente uno dei ruoli richiesti
      if (Array.isArray(requiredRole)) {
        hasRequiredRole = requiredRole.includes(userRole);
      } else {
        hasRequiredRole = userRole === requiredRole;
      }
    }
  }

  if (!hasRequiredRole) {
    // Use custom fallback if provided
    if (fallback) {
      return <>{fallback}</>;
    }

    // Default 403 Forbidden page
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <div className="max-w-md w-full">
          <Alert variant="destructive">
            <ShieldAlert className="h-4 w-4" />
            <AlertTitle>Accesso Negato</AlertTitle>
            <AlertDescription>
              Non hai i permessi necessari per accedere a questa sezione.
              Contatta l'amministratore se ritieni di aver bisogno dell'accesso.
            </AlertDescription>
          </Alert>
          <div className="mt-4 text-center">
            <a 
              href="/" 
              className="text-primary hover:underline"
            >
              Torna alla Home
            </a>
          </div>
        </div>
      </div>
    );
  }

  // Render protected content
  return <>{children}</>;
};
